<template>
  <Container class="container">
    <MSPPricingCard class="pricingBanner" />
    <CrowdstrikePricingCard class="pricingBanner" />
    <div>
      <HeadingHuge>
        Discover the plan that’s right for you
      </HeadingHuge>
      <HeadingSmall
        class="subheader"
        weight="normal"
      >
        <strong>Current Plan:</strong> Free - 10 users and devices
      </HeadingSmall>
    </div>
    <div>
      <AnnualMonthlyToggle
        name="PricingPage"
        :showSaveMore="true"
        @change="triggerAnnualMonthlyToggleEvent"
      />
      <div>
        <PricingMatrix
          :handleContactSales="(eventPayload) =>
            handleContactSalesClicked(eventPayload, 'Package Descriptions')"
          :isAnnual="isAnnual"
          :isPricingPage="true"
          @viewFeatureMatrix="handleViewFeatureMatrix"
        />
      </div>
    </div>
    <CollapsibleSection
      id="pricing-feature-matrix"
      buttonText="See Feature Pricing Matrix"
      class="featurePricingMatrixWrapper"
      :isExpanded="featurePricingMatrixExpanded"
      @expand="handleFeaturePricingMatrixExpanded"
    >
      <template v-slot:content>
        <div>
          <Banner
            v-if="$hasFeatureFlag($featureFlags.purchaseAddOnsInProduct)"
            bannerText="*Add-on features available with plan selections at checkout."
            bannerType="informational"
            :canCloseBanner="false"
            class="addOnsBanner"
            :data-test-id="$testId('addOnsBanner')"
            icon="info"
            :isMarkdown="false"
            showBanner
          />
          <FeaturePricingMatrix
            class="featurePricingMatrix"
            :isAnnual="isAnnual"
            @viewFeatureMatrix="handleViewFeatureMatrix"
          />
        </div>
      </template>
    </CollapsibleSection>
    <ContactSalesBanner
      @contactSales="openContactSalesModal"
    />
    <div>
      <Grid>
        <GridItem :gridSpan="6">
          <PricingCalculatorCard />
        </GridItem>
        <GridItem :gridSpan="6">
          <ImplementationGuideCard />
        </GridItem>
      </Grid>
    </div>
    <SearchOurKnowledgeBase
      class="searchOurKnowledgeBase"
    />
    <div class="faqSection">
      <PricingFaq />
    </div>
    <PricingFooter @fairUsePolicyClicked="handleFairUsePolicyClicked" />

    <ALaCarteCart
      @open-modal="openContactSalesModal"
    />

    <ContactSalesModal
      :showModal="showSalesModal"
      @close="showSalesModal = false"
    />
  </Container>
</template>

<script>

import {
  Banner,
  CollapsibleSection,
  Container,
  Grid,
  GridItem,
  HeadingHuge,
  HeadingSmall,
} from '@jumpcloud/ui-components';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import { eventLocations, eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters, mapState } from 'vuex';
import ALaCarteCart from 'client/Pricing/ALaCarte/ALaCarteCart.vue';
import AnnualMonthlyToggle from 'client/GrowthEngineering/components/AnnualMonthlyToggle.vue';
import ContactSalesBanner from 'client/Pricing/ContactSalesBanner.vue';
import ContactSalesModal from 'client/Pricing/ContactSalesModal/ContactSalesModal.vue';
import CrowdstrikePricingCard from 'client/Pricing/PricingCards/CrowdstrikePricingCard.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FeaturePricingMatrix from 'client/Pricing/FeaturePricingMatrix.vue';
import ImplementationGuideCard from 'client/Pricing/PricingCustomerSupport/ImplementationGuideCard.vue';
import MSPPricingCard from 'client/Pricing/PricingCards/MSPPricingCard.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';
import PricingCalculatorCard from 'client/Pricing/PricingCustomerSupport/PricingCalculatorCard.vue';
import PricingFaq from 'client/Pricing/PricingFaq.vue';
import PricingFooter from 'client/Pricing/PricingFooter.vue';
import PricingMatrix from 'client/Payments/pricingMatrix/PricingMatrix.vue';
import SearchOurKnowledgeBase from 'client/components/SearchOurKnowledgeBase/SearchOurKnowledgeBase.vue';
import Util from 'client/util/Util';

const checkoutModel = 'CheckoutModel';
const planModule = 'PlanModule';
const pricingModule = 'PricingModule';
const settingsModule = 'SettingsModule';

const {
  description,
  location,
  text,
  section,
} = eventProperties;

const {
  pricingPage,
} = eventLocations;

export default {
  name: 'PricingPage',

  EventHelper,
  ModalEventBus,
  Util,
  pricingPage,

  components: {
    ALaCarteCart,
    AnnualMonthlyToggle,
    Banner,
    CollapsibleSection,
    Container,
    ContactSalesBanner,
    ContactSalesModal,
    CrowdstrikePricingCard,
    FeaturePricingMatrix,
    Grid,
    GridItem,
    HeadingHuge,
    HeadingSmall,
    ImplementationGuideCard,
    MSPPricingCard,
    PricingCalculatorCard,
    PricingFaq,
    PricingFooter,
    PricingMatrix,
    SearchOurKnowledgeBase,
  },

  data() {
    return {
      featurePricingMatrixExpanded: true,
      showSalesModal: false,
    };
  },

  computed: {
    ...mapGetters(planModule, [
      'hasAnnualContractType',
    ]),

    ...mapGetters(pricingModule, {
      isAnnual: 'getIsAnnual',
    }),

    ...mapState(settingsModule, [
      'isCustomer',
    ]),

    ...mapGetters(checkoutModel, {
      subscription: 'item',
    }),

    billingType() {
      return this.isAnnual ? 'Annual Billing' : 'Monthly Billing';
    },
  },

  async created() {
    this.setIsAnnual(true);
  },

  methods: {
    ...mapActions(pricingModule, ['setIsAnnual']),

    handleContactSalesClicked(subDisplayName, sectionName) {
      const sectionInfo = subDisplayName ? `${sectionName}: ${subDisplayName}: ${this.billingType}` : sectionName;
      this.openContactSalesModal();
      this.triggerContactSalesEvent({
        [section]: sectionInfo,
      });
    },

    handleFairUsePolicyClicked(eventPayload) {
      this.$options.EventHelper.triggerLinkClicked({
        ...eventPayload,
        [location]: pricingPage,
        [section]: 'Pricing Footer',
      });
    },

    handleFeaturePricingMatrixExpanded() {
      this.featurePricingMatrixExpanded = !this.featurePricingMatrixExpanded;
    },

    async handleViewFeatureMatrix() {
      this.featurePricingMatrixExpanded = true;
      await this.$nextTick();
      document.getElementById('pricing-feature-matrix')
        .scrollIntoView({
          behavior: 'smooth',
        });
    },

    openContactSalesModal() {
      this.showSalesModal = true;
    },

    triggerAnnualMonthlyToggleEvent(billingType) {
      UiEvents.triggerToggleClicked({
        description: 'annual/monthly toggle clicked',
        page: this.$page,
        section: `billing term selection: ${billingType}`,
        text: 'Annual/Monthly',
        value: billingType,
      });
    },

    triggerContactSalesEvent(eventPayload) {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'open contact sales modal',
        [location]: pricingPage,
        [text]: 'contact sales',
        ...eventPayload,
      });
    },
  },
};
</script>

<style scoped>

.addOnsBanner {
  margin-bottom: var(--jc-spacer-small);
  text-align: left;
}

.container {
  display: flex;
  flex-direction: column;
  gap: var(--jc-spacer-large);
  text-align: center;
}

.customerSupportSection {
  margin: 3rem 0;
}

.faqSection {
  margin: 3rem auto;
  max-width: 85%;
}

.featurePricingMatrix {
  display: initial;
  text-align: initial;
}

.featurePricingMatrixWrapper {
  margin: 0 auto;
  width: fit-content;
}

.free {
  font-weight: 800;
}

.pricingBanner {
  margin-bottom: var(--jc-spacer-large);
}

.searchOurKnowledgeBase {
  margin-bottom: 3rem;
  margin-top: var(--jc-spacer-negative-medium);
}

.subheader {
  color: var(--jc-text-color-light);
}

.yourAccount {
  font-weight: 600;
}

.yourAccountIsFree {
  margin: var(--jc-spacer) 0;
}
</style>
